import { useExperienceApi } from '@/shared/api/useExperienceApi.ts';
import { useQuery } from '@tanstack/vue-query';
import type { GetExperiencesData } from '@/shared/api/experience';
import { useUnlicensedContentFilter } from '@/shared/composables/useUnlicensedContentFilter.ts';
import { useDisplayCurrencyQueryParams } from '@/shared/composables/useDisplayCurrencyQueryParams.ts';
import { useListingLabelsV4Experiment } from '@/features/experiments';

export const useGetExperiences = (params: Ref<GetExperiencesData['query']>, enabled?: MaybeRefOrGetter<boolean>) => {
  const { getExperiences } = useExperienceApi();
  const { isListingLabelsV4ExperimentActive } = useListingLabelsV4Experiment();
  const { hideUnlicensed } = useUnlicensedContentFilter();
  const displayCurrencyQueryParams = useDisplayCurrencyQueryParams();

  const experiencesQueryKeyParams = computed(() => ({
    ...params.value,
    ...displayCurrencyQueryParams.value,
  }));

  const {
    data: experiencesData,
    isFetching,
    isFetched,
  } = useQuery({
    queryKey: computed(() => ['getExperiences', experiencesQueryKeyParams.value]),
    queryFn: async () => {
      const response = await getExperiences({
        query: {
          ...experiencesQueryKeyParams.value,
          extended_movement_types: true,
          exclude_needs_license: hideUnlicensed.value,
          include_labels: isListingLabelsV4ExperimentActive.value,
          photos_count: 1,
        },
      });

      return response.data;
    },
    enabled: computed(() => !!unref(enabled !== undefined ? enabled : computed(() => true))),
    placeholderData: (prevData, previousQuery) => {
      const prevParams = previousQuery?.queryKey?.[1] as { display_currency?: string } | undefined;
      const nextCurrency = experiencesQueryKeyParams.value.display_currency;

      if (prevParams?.display_currency !== nextCurrency) {
        return undefined;
      }

      return prevData ?? { count: 0, next: null, previous: null, results: [] };
    },
    refetchOnWindowFocus: false,
  });

  return {
    experiencesData,
    isFetching,
    isFetched,
  };
};
